// src/SparepartsPages/Dpo/DpoDashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  Drawer,
  Tabs,
  Tab,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* helpers */
const fmt = (v) => (v ? String(v).slice(0, 10) : "");
const num = (v) => (v === null || v === undefined ? 0 : Number(v) || 0);

const StatusChip = ({ v }) => {
  if (!v) return <Chip size="small" label="-" />;
  const color =
    v === "ALL_PRESENT"
      ? "success"
      : String(v).startsWith("MISSING_")
        ? "error"
        : "secondary";
  return <Chip size="small" color={color} label={v} />;
};

const PresentChip = ({ ok, label }) => (
  <Chip
    size="small"
    sx={{ mr: 0.5, mb: 0.5 }}
    color={ok ? "success" : "error"}
    variant={ok ? "outlined" : "filled"}
    label={ok ? label : `MISSING ${label}`}
  />
);

const DpoDashboard = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const isLoggedIn = !!userToken;

  setTimeout(() => window.location.reload(false), 1800000);

  /* search + purchased filter */
  const [search, setSearch] = useState("");
  const [purchased, setPurchased] = useState("ALL"); // ALL|YES|NO

  /* ✅ NEW: status filter (invoice list) */
  const [status, setStatus] = useState("ALL"); // ALL | ALL_PRESENT | MISSING_MAP | MISSING_TRANSIT | MISSING_CASE | OTHER

  /* grid */
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [gridLoading, setGridLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState([
    { field: "last_activity", sort: "desc" },
  ]);

  /* invoice drawer */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [activeInv, setActiveInv] = useState(null);

  const [dposLoading, setDposLoading] = useState(false);
  const [dpos, setDpos] = useState([]);

  const [trItemsLoading, setTrItemsLoading] = useState(false);
  const [trItems, setTrItems] = useState([]);

  const [csItemsLoading, setCsItemsLoading] = useState(false);
  const [csItems, setCsItems] = useState([]);

  /* dpo modal */
  const [dpoModalOpen, setDpoModalOpen] = useState(false);
  const [activeDpo, setActiveDpo] = useState(null);
  const [dpoItemsLoading, setDpoItemsLoading] = useState(false);
  const [dpoItems, setDpoItems] = useState([]);

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { sptoken: userToken, ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Request failed");
    return data;
  };

  const baseQS = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    p.set("purchased", purchased);

    // ✅ status filter
    if (status && status !== "ALL") p.set("status", status);

    return p.toString();
  }, [search, purchased, status]);

  const loadGrid = useCallback(async () => {
    setGridLoading(true);
    try {
      const sm = sortModel?.[0] || { field: "last_activity", sort: "desc" };
      const p = new URLSearchParams(baseQS);
      p.set("page", page);
      p.set("pageSize", pageSize);
      p.set("sortBy", sm.field || "last_activity");
      p.set("sortDir", sm.sort || "desc");

      const data = await fetchJSON(`/dpo/dashboard/invlist?${p.toString()}`);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setRowCount(Number(data.rowCount || 0));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGridLoading(false);
    }
  }, [baseQS, page, pageSize, sortModel]);

  const apply = async () => {
    setPage(0);
    await loadGrid();
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    loadGrid();
  }, [loadGrid, isLoggedIn]);

  const openInvoice = async (row) => {
    if (!row?.invno) return;

    setActiveInv(row);
    setDrawerOpen(true);
    setDrawerTab(0);

    setDpos([]);
    setTrItems([]);
    setCsItems([]);

    setDposLoading(true);
    setTrItemsLoading(true);
    setCsItemsLoading(true);

    try {
      const [dpoRes, trRes, csRes] = await Promise.all([
        fetchJSON(
          `/dpo/dashboard/invoice/${encodeURIComponent(row.invno)}/dpos`
        ),
        fetchJSON(
          `/dpo/dashboard/invoice/${encodeURIComponent(
            row.invno
          )}/transit-items`
        ),
        fetchJSON(
          `/dpo/dashboard/invoice/${encodeURIComponent(row.invno)}/case-items`
        ),
      ]);

      setDpos(dpoRes.rows || []);
      setTrItems(trRes.rows || []);
      setCsItems(csRes.rows || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDposLoading(false);
      setTrItemsLoading(false);
      setCsItemsLoading(false);
    }
  };

  const openDpoModal = async (dpoRow) => {
    if (!dpoRow?.dpono) return;
    setActiveDpo(dpoRow);
    setDpoModalOpen(true);
    setDpoItems([]);
    setDpoItemsLoading(true);

    try {
      const r = await fetchJSON(
        `/dpo/dashboard/dpo/${encodeURIComponent(dpoRow.dpono)}/items`
      );
      setDpoItems(r.rows || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDpoItemsLoading(false);
    }
  };

  const invCols = [
    {
      field: "invno",
      headerName: "Invoice No",
      minWidth: 220,
      flex: 1,
      renderCell: (p) => (
        <Button size="small" onClick={() => openInvoice(p.row)}>
          {p.value}
        </Button>
      ),
    },
    {
      field: "invdate",
      headerName: "Inv Date",
      minWidth: 110,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      field: "purchased",
      headerName: "Purchased",
      minWidth: 110,
      renderCell: (p) =>
        p.value ? (
          <Chip size="small" color="success" label="YES" />
        ) : (
          <Chip size="small" label="NO" />
        ),
    },
    {
      field: "status",
      headerName: "Status (Map/Transit/Case)",
      minWidth: 260,
      renderCell: (p) => <StatusChip v={p.value} />,
    },
    {
      field: "presence",
      headerName: "Presence",
      minWidth: 420,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <PresentChip ok={!!p.row.has_map} label="DpoInvMap" />
          <PresentChip ok={!!p.row.has_transit} label="Transit" />
          <PresentChip ok={!!p.row.has_case} label="Case" />
        </Box>
      ),
    },
  ];

  const dpoCols = [
    { field: "dpono", headerName: "DPO", minWidth: 260, flex: 1 },
    {
      field: "dpodate",
      headerName: "DPO Date",
      minWidth: 110,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      field: "partyname",
      headerName: "Party",
      minWidth: 260,
      flex: 1,
      renderCell: (p) =>
        p.row.isstock ? (
          <Chip size="small" label="STOCK" />
        ) : (
          <Chip size="small" color="success" label={p.value || "STOCK"} />
        ),
    },
    {
      field: "status",
      headerName: "Mapped?",
      minWidth: 140,
      renderCell: (p) =>
        p.value === "DPO_UPLOADED" ? (
          <Chip size="small" color="success" label="MAPPED" />
        ) : (
          <Chip size="small" color="error" label="NOT MAPPED" />
        ),
    },
    {
      field: "action",
      headerName: "Open",
      minWidth: 110,
      sortable: false,
      renderCell: (p) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => openDpoModal(p.row)}
        >
          View
        </Button>
      ),
    },
  ];

  if (!isLoggedIn) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2000} newestOnTop />
      <Sidebar
        Title={"Invoice Dashboard"}
        expandedPanel={"panel2"}
        children={
          <Box sx={{ p: 1 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                sx={{
                  py: 1,
                  "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 700 },
                }}
                title="Transit Invoice Dashboard (linked with DpoInvMap + Case)"
                subheader={
                  <Typography variant="caption">
                    Main list is from Transit • Search optional • Filter
                    Purchased based on inwardid • Filter Status based on
                    Map/Transit/Case presence
                  </Typography>
                }
              />
              <Divider />
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Search Invoice No (optional)"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Purchased"
                      value={purchased}
                      onChange={(e) => setPurchased(e.target.value)}
                    >
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="YES">YES</MenuItem>
                      <MenuItem value="NO">NO</MenuItem>
                    </TextField>
                  </Grid>

                  {/* ✅ NEW STATUS FILTER */}
                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="ALL_PRESENT">ALL_PRESENT</MenuItem>
                      <MenuItem value="MISSING_MAP">MISSING_MAP</MenuItem>
                      <MenuItem value="MISSING_TRANSIT">
                        MISSING_TRANSIT
                      </MenuItem>
                      <MenuItem value="MISSING_CASE">MISSING_CASE</MenuItem>
                      <MenuItem value="OTHER">OTHER</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={apply}
                      disabled={gridLoading}
                      fullWidth
                    >
                      Apply
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    {gridLoading && <CircularProgress size={18} />}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ height: 620 }}>
                  <DataGrid
                    rows={rows}
                    columns={invCols}
                    getRowId={(r) => r.invno}
                    density="compact"
                    rowHeight={32}
                    loading={gridLoading}
                    rowCount={rowCount}
                    pagination
                    paginationMode="server"
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(p) => setPage(p)}
                    onPageSizeChange={(s) => {
                      setPageSize(s);
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={(m) => setSortModel(m)}
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                      },
                    }}
                  />
                </Box>

                {/* Invoice Drawer */}
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{ sx: { width: { xs: "100%", md: 920 } } }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                          Invoice: {activeInv?.invno || "-"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          sx={{ mt: 0.5 }}
                        >
                          <Chip
                            size="small"
                            label={`InvDate: ${fmt(activeInv?.invdate) || "-"}`}
                          />
                          <Chip
                            size="small"
                            label={`Purchased: ${activeInv?.purchased ? "YES" : "NO"}`}
                          />
                          <StatusChip v={activeInv?.status} />
                          <PresentChip ok={!!activeInv?.has_map} label="DpoInvMap" />
                          <PresentChip ok={!!activeInv?.has_transit} label="Transit" />
                          <PresentChip ok={!!activeInv?.has_case} label="Case" />
                        </Stack>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDrawerOpen(false)}
                      >
                        Close
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Tabs value={drawerTab} onChange={(e, v) => setDrawerTab(v)}>
                      <Tab label="DPOs" />
                      <Tab label="Transit Items" />
                      <Tab label="Case Items" />
                    </Tabs>

                    {drawerTab === 0 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {dposLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={dpos}
                            getRowId={(r) => r.dpono}
                            columns={dpoCols}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {drawerTab === 1 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {trItemsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={trItems}
                            getRowId={(r) => r.partno}
                            columns={[
                              { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
                              { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.2 },
                              { field: "qty", headerName: "Qty", width: 110, type: "number" },
                              { field: "value", headerName: "Value", width: 130, type: "number" },
                            ]}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {drawerTab === 2 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {csItemsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={csItems}
                            getRowId={(r) => r.partno}
                            columns={[
                              { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
                              { field: "itemname", headerName: "Item", minWidth: 300, flex: 1.4 },
                              { field: "qty", headerName: "Qty", width: 110, type: "number" },
                              { field: "racknames", headerName: "Rack", minWidth: 220, flex: 1 },
                            ]}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Drawer>

                {/* DPO Modal */}
                <Dialog
                  open={dpoModalOpen}
                  onClose={() => setDpoModalOpen(false)}
                  maxWidth="lg"
                  fullWidth
                >
                  <DialogTitle sx={{ pr: 6 }}>
                    DPO: {activeDpo?.dpono || "-"}
                    <IconButton
                      onClick={() => setDpoModalOpen(false)}
                      sx={{ position: "absolute", right: 10, top: 10 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent dividers>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        size="small"
                        label={`DPO Date: ${fmt(activeDpo?.dpodate) || "-"}`}
                      />
                      <Chip
                        size="small"
                        label={`Party: ${activeDpo?.isstock ? "STOCK" : activeDpo?.partyname || "STOCK"}`}
                      />
                      <Chip
                        size="small"
                        color={activeDpo?.status === "DPO_UPLOADED" ? "success" : "error"}
                        label={activeDpo?.status === "DPO_UPLOADED" ? "MAPPED" : "NOT MAPPED"}
                      />
                    </Stack>

                    <Box sx={{ height: 520 }}>
                      {dpoItemsLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DataGrid
                          rows={dpoItems}
                          getRowId={(r) => r.partno || `${r.itemname}-${Math.random()}`}
                          columns={[
                            { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
                            { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.3 },
                            { field: "dpo_qty", headerName: "Inv Qty", width: 120, type: "number" },
                          ]}
                          density="compact"
                          rowHeight={32}
                          disableRowSelectionOnClick
                          slots={{ toolbar: GridToolbar }}
                          slotProps={{
                            toolbar: {
                              showQuickFilter: true,
                              quickFilterProps: { debounceMs: 300 },
                            },
                          }}
                        />
                      )}
                    </Box>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default DpoDashboard;
